import { EFFECTS_FOR, RADIUS_VALUES, SECTION_ENTERS, TEXT_STYLE_KEY_PATTERN, TEXT_SIZE_SCALE, TRACKING_VALUES, type SectionStyle, type TextStyle } from "@baze/schema";
import { fontStack, type EngineTheme } from "./theme";

// Defense in depth like theme.ts: only validated #RRGGBB values are ever interpolated into CSS.
const HEX = /^#[0-9a-fA-F]{6}$/;
const ANGLES = new Set([0, 45, 90, 135, 180, 225, 270, 315]);
const PATTERNS = new Set(["dots", "grid", "lines", "noise"]);
const SCALES = new Set(["sm", "lg", "xl"]);
const ENTERS = new Set<string>(SECTION_ENTERS);
const SPEEDS = new Set(["slow", "fast"]);

const hex = (v: string | undefined) => (v && HEX.test(v) ? v : null);

const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];

function luminance(h: string): number {
  const [r, g, b] = rgb(h).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The average of a few colours (what "auto" text is measured against on gradients). */
function average(colors: string[]): string {
  const sum = colors.map(rgb).reduce((a, c) => a.map((v, i) => v + c[i]!) as [number, number, number], [0, 0, 0]);
  return "#" + sum.map((v) => Math.round(v / colors.length).toString(16).padStart(2, "0")).join("");
}

const DARK = "#0F172A";
const LIGHT = "#FFFFFF";
/** Dark or white text, whichever contrasts more with `bg` (WCAG relative luminance). */
export function readableOn(bg: string): string {
  const l = luminance(bg);
  return (l + 0.05) / (luminance(DARK) + 0.05) >= (1.05) / (l + 0.05) ? DARK : LIGHT;
}

export interface SectionStyleAttrs {
  data: Record<string, string>;
  vars: Record<string, string>;
}

/**
 * Data attributes (selectors in styles.css) and scoped CSS variables for a section's owner styling.
 * Invalid values are dropped, so a bad colour renders the design's look instead of broken CSS.
 */
export function sectionStyleAttrs(style: SectionStyle | undefined, theme?: EngineTheme | null, type?: string): SectionStyleAttrs {
  const data: Record<string, string> = {};
  const vars: Record<string, string> = {};
  if (!style) return { data, vars };
  sectionTheme(style.theme, theme, data, vars);
  let base: string[] = [];
  const bg = style.bg;
  if (bg?.kind === "solid" && hex(bg.color)) {
    data["data-style-bg"] = "solid";
    vars["--site-bg"] = bg.color;
    base = [bg.color];
  } else if (bg?.kind === "gradient" && hex(bg.from) && hex(bg.to) && ANGLES.has(bg.angle)) {
    data["data-style-bg"] = "gradient";
    Object.assign(vars, { "--site-bg": average([bg.from, bg.to]), "--sec-from": bg.from, "--sec-to": bg.to, "--sec-angle": `${bg.angle}deg` });
    base = [bg.from, bg.to];
  } else if (bg?.kind === "mesh" && hex(bg.a) && hex(bg.b) && hex(bg.c)) {
    data["data-style-bg"] = "mesh";
    Object.assign(vars, { "--site-bg": bg.a, "--sec-a": bg.a, "--sec-b": bg.b, "--sec-c": bg.c });
    base = [bg.a, bg.b, bg.c];
  }
  const text = style.text === "auto" ? (base.length ? readableOn(average(base)) : null) : hex(style.text);
  if (text) {
    vars["--site-text"] = text;
    vars["--site-muted"] = `color-mix(in srgb, ${text} 72%, var(--site-bg))`;
    vars["--site-border"] = `color-mix(in srgb, ${text} 16%, var(--site-bg))`;
    vars["--site-surface"] = `color-mix(in srgb, ${text} 6%, var(--site-bg))`;
  }
  if (style.pattern && PATTERNS.has(style.pattern)) data["data-pattern"] = style.pattern;
  if (style.headingScale && SCALES.has(style.headingScale)) data["data-heading-scale"] = style.headingScale;
  if (style.align === "center") data["data-align"] = "center";
  motionAttrs(style.motion, type, data);
  return { data, vars };
}

/**
 * Per-section motion as data attributes (styles.css and runtime/motion.ts key off them): `data-enter`,
 * `data-fx` (space-separated, only the effects that apply to the section type) and `data-speed`.
 */
function motionAttrs(m: SectionStyle["motion"], type: string | undefined, data: Record<string, string>) {
  if (!m) return;
  if (m.enter && ENTERS.has(m.enter)) data["data-enter"] = m.enter;
  const allowed = type ? EFFECTS_FOR[type] : undefined;
  const fx = Array.isArray(m.effects) ? [...new Set(m.effects)].filter((e) => allowed?.includes(e)).slice(0, 4) : [];
  if (fx.length) data["data-fx"] = fx.join(" ");
  if (m.speed && SPEEDS.has(m.speed)) data["data-speed"] = m.speed;
}

const EFFECTS = new Set(["highlight", "underline", "wavy", "circle", "gradient", "italic", "outline", "box"]);

/** [before, phrase, after] around the `nth` (1-based) occurrence of `phrase` in `title`, or null. */
export function splitEmphasis(title: string, phrase: string, nth = 1): [string, string, string] | null {
  if (!phrase) return null;
  let at = -1;
  for (let i = 0; i < Math.max(1, nth); i++) {
    at = title.indexOf(phrase, at + 1);
    if (at < 0) return null;
  }
  return [title.slice(0, at), phrase, title.slice(at + phrase.length)];
}

/** Title split for its emphasis, with the effect's attributes; null when there is nothing to mark. */
export function emphasisParts(title: string, style: SectionStyle | undefined, fallbackColor: string) {
  const em = style?.emphasis;
  if (!em || !EFFECTS.has(em.effect)) return null;
  const parts = splitEmphasis(title, em.text, em.nth);
  if (!parts) return null;
  const color = hex(em.color) ?? hex(fallbackColor);
  const vars: Record<string, string> = {};
  if (hex(em.color)) vars["--em"] = em.color!;
  if (color) vars["--em-on"] = readableOn(color);
  return { parts, effect: em.effect, vars };
}

const WEIGHTS = new Set([300, 400, 500, 600, 700, 800, 900]);
const CASES: Record<string, string> = { none: "none", uppercase: "uppercase", lowercase: "lowercase", capitalize: "capitalize" };
const ALIGNS = new Set(["start", "center", "end"]);
const RADII = RADIUS_VALUES as Record<string, (typeof RADIUS_VALUES)["soft"]>;
const TRACKS = TRACKING_VALUES as Record<string, string>;

const fontById = (theme: EngineTheme | null | undefined, id: string | undefined) => (id ? theme?.library?.fonts.find((f) => f.id === id) : undefined);

/** The section's own theme (section.style.theme) as scoped variables; children inherit them. */
function sectionTheme(t: SectionStyle["theme"], theme: EngineTheme | null | undefined, data: Record<string, string>, vars: Record<string, string>) {
  if (!t) return;
  const primary = hex(t.primary);
  if (primary) {
    vars["--site-primary"] = primary;
    vars["--site-on-primary"] = readableOn(primary);
  }
  const accent = hex(t.accent);
  if (accent) vars["--site-accent"] = accent;
  const pair = t.fontPair ? theme?.library?.pairs.find((p) => p.id === t.fontPair) : undefined;
  const heading = fontById(theme, pair?.heading);
  const body = fontById(theme, pair?.body);
  if (heading && body) {
    vars["--site-font-heading"] = fontStack(heading);
    vars["--site-font-body"] = fontStack(body);
    data["data-style-font"] = "";
  }
  if (t.headingWeight && WEIGHTS.has(t.headingWeight)) vars["--site-heading-weight"] = String(t.headingWeight);
  if (t.headingCase) vars["--site-heading-case"] = t.headingCase === "uppercase" ? "uppercase" : "none";
  if (t.headingTracking && TRACKS[t.headingTracking]) vars["--site-heading-tracking"] = TRACKS[t.headingTracking]!;
  const r = t.radius ? RADII[t.radius] : undefined;
  if (r) Object.assign(vars, { "--site-radius-sm": r.sm, "--site-radius-md": r.md, "--site-radius-lg": r.lg, "--site-radius-button": r.button });
}

/** CSS declarations for one text style; anything unknown is skipped. */
export function textDeclarations(s: TextStyle, theme?: EngineTheme | null): string {
  const out: string[] = [];
  const font = fontById(theme, s.font);
  if (font) out.push(`font-family:${fontStack(font)}`);
  const scale = s.size ? (TEXT_SIZE_SCALE as Record<string, number>)[s.size] : undefined;
  if (scale && scale !== 1) out.push(`zoom:${scale}`);
  if (s.weight && WEIGHTS.has(s.weight)) out.push(`font-weight:${s.weight}`);
  if (s.italic === true) out.push("font-style:italic");
  if (s.italic === false) out.push("font-style:normal");
  if (s.underline) out.push("text-decoration-line:underline", "text-underline-offset:0.18em", "text-decoration-thickness:0.06em");
  if (s.case && CASES[s.case]) out.push(`text-transform:${CASES[s.case]}`);
  if (s.tracking && TRACKS[s.tracking]) out.push(`letter-spacing:${TRACKS[s.tracking]}`);
  const color = hex(s.color);
  if (color) out.push(`color:${color}`);
  if (s.align && ALIGNS.has(s.align)) out.push(`text-align:${s.align}`);
  return out.join(";");
}

/**
 * A <style> body for the section's text styles, scoped to its element id. `*` keys come first so a
 * style for one exact item wins over the one for every item. Keys and values are validated, so the
 * output never carries owner text.
 */
export function textStyleCss(scopeId: string, texts: SectionStyle["texts"], theme?: EngineTheme | null): string {
  if (!texts || typeof texts !== "object" || !/^[a-z0-9-]+$/i.test(scopeId)) return "";
  const entries = Object.entries(texts).filter(([k]) => TEXT_STYLE_KEY_PATTERN.test(k));
  entries.sort(([a], [b]) => Number(!a.includes("*")) - Number(!b.includes("*")));
  return entries
    .map(([key, s]) => {
      const decl = s && typeof s === "object" ? textDeclarations(s, theme) : "";
      return decl ? `#${scopeId} [${key.includes("*") ? "data-tg" : "data-tf"}="${key}"]{${decl}}` : "";
    })
    .join("");
}
